import { type FunctionComponent, type ReactNode, useState } from 'react';

import { Button, Dropdown } from '@dr.pogodin/react-utils';
import { captureException } from '@sentry/react';

import InputVolume from 'components/InputVolume';
import Throbber from 'components/Throbber';

import { useDevices, useInput, useOutput } from 'hooks/audio';

import { getAudioClientService } from 'services';
import { speech } from 'services/ai';
import { updateUser } from 'services/user';

import {
  DEFAULT_VOICE,
  VOICES,
  type VoiceT,
  getId,
  useGlobalState,
} from 'utils';

import IconCross from 'assets/images/icon-cross-24px.svg';

import IconMicrophone from 'assets/images/icon-microphone-60px.svg';

import iconButtonTheme from 'themes/Button/icon-48px.scss';

import { AudioInput } from './AudioInput';
import { AudioOutput } from './AudioOutput';

import dropdownTheme from './dropdown.scss';

import S from './style.scss';

const AUDIO_TEST_TEXT = `Welcome to Soul Sanctuary!

Your voice is how we connect.

Soul Sanctuary uses your microphone so you can speak naturally during your wellness check-in. Your browser
will ask for permission - select "Allow while visiting this site" to get started.

If audio isn't working in the future, check Audio Settings from the Audio icon on the dashboard.`;

const RecoveryInstructions: FunctionComponent = () => {
  // NOTE: As deducing user's device & browser is notoriously unreliable,
  // we just check the screen size (on which the browser is rendered), and
  // make decision based on it.

  // Assuming it is a desktop.
  if (window.screen.width > 1024) {
    return (
      <ol>
        <li>Click the lock icon in your address bar</li>
        <li>Set Microphone to Allow</li>
        <li>Refresh the page</li>
      </ol>
    );
  }

  // Otherwise, assuming it is a mobile or tablet device.
  return (
    <ol>
      <li>Open your device&apos;s Settings app </li>
      <li>Find your browser</li>
      <li>Enable Microphone</li>
      <li>Come back and refresh this page</li>
    </ol>
  );
};

/**
 * The audio settings "page". For now it is not mounted at a dedicated route,
 * it is just rendered in-place of any other page, if microphone settings are
 * necessary to be checked.
 */
const AudioSetup: FunctionComponent = () => {
  const devices = useDevices();
  const input = useInput();
  const output = useOutput();

  const [blockedAudioDeviceAccess] = useGlobalState(
    'ui.blockedAudioDeviceAccess',
  );

  const [user, setUser] = useGlobalState('user');
  if (!user) throw Error('Unauthenticated');

  const [
    testAudioBuffer,
    setTestAudioBuffer,
  ] = useState<'pending' | AudioBuffer>();

  const [, setAudioSetup] = useGlobalState('ui.audioSetup');

  let testAudioNode: ReactNode;

  if (testAudioBuffer === 'pending') {
    testAudioNode = <div styleName="centered"><Throbber /></div>;
  } else if (testAudioBuffer) {
    testAudioNode = (
      <InputVolume
        onEnded={() => {
          setTestAudioBuffer(undefined);
        }}
        source={testAudioBuffer}
        withOutput
      />
    );
  } else {
    testAudioNode = (
      <Button
        onClick={() => {
          void (async () => {
            setTestAudioBuffer('pending');
            const buffer = await speech(
              AUDIO_TEST_TEXT,
              { voice: user.voice ?? DEFAULT_VOICE },
            );
            setTestAudioBuffer(buffer);
          })();
        }}
      >
        Test audio output
      </Button>
    );
  }

  const setInputByLabel = (label: string) => {
    const device = devices.input.find((item) => item.label === label);
    if (!device) throw Error('Internal error');

    void (async () => {
      const audio = await getAudioClientService();
      audio.setInputDevice(device.deviceId);
      input.refresh();
    })();
  };

  const setOutputByLabel = (label: string) => {
    const device = devices.output.find((item) => item.label === label);

    if (device) {
      void (async () => {
        const audio = await getAudioClientService();
        await audio.setOutputDevice(device.deviceId);
      })();

    // TODO: I thought, the way we select one of available audio devices
    // it should never happen, but Grant somehow ran into this situation
    // on his mobile... thus, we should investigate further.
    } else captureException(Error(`Selected device "${label}" not found`));
  };

  const canClose: boolean = !blockedAudioDeviceAccess && !!input.label;

  let content: ReactNode = blockedAudioDeviceAccess ? (
    <div>
      <p>Microphone access is needed to get started.</p>
      <p>It looks like access was declined. Here&apos;s how to fix it:</p>
      <RecoveryInstructions />
    </div>
  ) : (
    <div>
      <p>Your voice is how we connect.</p>
      <p>
        Soul Sanctuary uses your microphone so you can speak naturally during
        your wellness check-in. Your browser will ask for permission &mdash;
        select &ldquo;Allow while visiting this site&rdquo; to get started.
      </p>
      <p>
        If audio isn&apos;t working in the future, check Audio Settings from
        the Audio icon on the dashboard.
      </p>
    </div>
  );

  content = (
    <>
      {content}
      <div styleName="devices">
        <AudioInput
          devices={devices.input}
          setInputByLabel={setInputByLabel}
        />
        <div styleName="group">
          <AudioOutput
            devices={devices.output}
            setOutputByLabel={setOutputByLabel}
          />
          <Dropdown
            label="Voice"
            onChange={(e) => {
              void (async () => {
                try {
                  const newVoice = e.target.value as VoiceT;
                  await updateUser(getId(user), { voice: newVoice });
                  setUser((prev) => {
                    if (!prev) throw Error('Inernal error');
                    return { ...prev, voice: newVoice };
                  });
                } catch {
                  // eslint-disable-next-line no-alert
                  alert('Failed to switch the voice');
                }
              })();
            }}
            options={VOICES}
            theme={dropdownTheme}
            value={user.voice ?? DEFAULT_VOICE}
          />
          {testAudioNode}
          <Button
            onClick={() => {
              devices.refresh();
              input.refresh();
              output.refresh();
            }}
          >
            Refresh devices
          </Button>
        </div>
      </div>
      <Button
        disabled={!canClose}
        onClick={() => {
          try {
            setInputByLabel(input.label);
            if (output.label) setOutputByLabel(output.label);
            setAudioSetup(false);
          } catch {
            // eslint-disable-next-line no-alert
            alert('Failed to get access to audio devices!');
          }
        }}
      >
        All set
      </Button>
    </>
  );

  return (
    <div className={S.container}>
      {canClose ? (
        <div styleName="closeButton">
          <Button
            onClick={() => {
              setAudioSetup(false);
            }}
            theme={iconButtonTheme}
          >
            <IconCross />
          </Button>
        </div>
      ) : null}
      <IconMicrophone />
      <h1>Welcome to Soul Sanctuary.</h1>
      {content}
    </div>
  );
};

export default AudioSetup;
